import { Button, Card, Flex, Text, Title } from '@mantine/core';
import { IconCheck, IconPackages } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useBaleOutboundContext } from '../hook';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Success page displayed after successful bale outbound load submission.
 *
 * This page:
 * - Shows animated success confirmation
 * - Displays a summary of the processed load
 * - Provides options to process another load or return to facility dashboard
 * - Uses smooth animations to provide satisfying user feedback
 */
export const BaleOutboundSuccessPage = () => {
	const navigate = useNavigate();
	// ========================================================================
	// CONTEXT AND DATA
	// ========================================================================

	const { formData, scannedBales, resetWorkflow } = useBaleOutboundContext();

	// Calculate summary data
	const totalBales = scannedBales
		.filter((bale) => bale.count > 0)
		.reduce((sum, bale) => sum + bale.count, 0);

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle starting a new load processing workflow
	 */
	const handleProcessAnother = () => {
		// Reset all workflow state and navigate back to root
		resetWorkflow();
	};

	/**
	 * Handle returning to facility dashboard
	 * Note: This would typically navigate to the parent route
	 */
	const handleReturnToFacility = () => {
		// For now, reset workflow - in a real app this might navigate up a level
		navigate({ to: '/facility/home' });
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<Flex
			h="100vh"
			direction="column"
			style={{
				backgroundColor: 'var(--mantine-color-green-0)',
				position: 'relative',
			}}
		>
			{/* Scrollable Content Area */}
			<Flex
				direction="column"
				align="center"
				px="lg"
				pt="xl"
				pb={180}
				style={{
					flex: 1,
					overflowY: 'auto',
					justifyContent: 'flex-start',
				}}
			>
				<motion.div
					initial={{ scale: 0, rotate: -180 }}
					animate={{ scale: 1, rotate: 0 }}
					transition={{
						type: 'spring',
						damping: 15,
						stiffness: 300,
						delay: 0.2,
					}}
					style={{ marginBottom: '32px', marginTop: '20px' }}
				>
					<Flex
						align="center"
						justify="center"
						w={80}
						h={80}
						style={{
							backgroundColor: 'var(--mantine-color-green-0)',
							borderRadius: '50%',
							border: '2px solid var(--mantine-color-green-3)',
						}}
					>
						<IconCheck size={40} color="var(--mantine-color-green-3)" />
					</Flex>
				</motion.div>

				{/* Success Title - Mobile optimized */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.4 }}
					style={{ marginBottom: '16px' }}
				>
					<Title order={2} ta="center">
						Load Successfully Processed!
					</Title>
				</motion.div>

				{/* Description Text */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4, delay: 0.5 }}
					style={{ marginBottom: '32px' }}
				>
					<Text size="sm" ta="center">
						Your outbound load has been successfully recorded and all photos have been uploaded.
					</Text>
				</motion.div>

				{/* Load Summary - Simplified for mobile */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.6 }}
					style={{ width: '100%', marginBottom: '24px' }}
				>
					<Card shadow="sm" padding="lg" radius="md">
						<Flex align="center" gap="sm" mb="lg">
							<IconPackages size={24} color="var(--mantine-color-green-6)" />
							<Text size="lg" fw={600} c="dark">
								Load Summary
							</Text>
						</Flex>

						<Flex direction="column" align="center" gap="md" w="100%">
							<Flex direction="column" align="center">
								<Text size="xl" fw={700} c="dark" mb={4}>
									{totalBales}
								</Text>
								<Text size="md" c="dimmed">
									{totalBales === 1 ? 'bale processed' : 'bales processed'}
								</Text>
							</Flex>

							{formData.weight && (
								<Flex direction="column" align="center">
									<Text size="lg" fw={600} c="dark" mb={4}>
										{(formData.weight / 1000).toFixed(1)} Tonne
									</Text>
									<Text size="sm" c="dimmed">
										Total weight
									</Text>
								</Flex>
							)}
						</Flex>
					</Card>
				</motion.div>

				{/* Status Message */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.8 }}
				>
					<Text size="sm" ta="center">
						The load information has been saved and is now available in your facility records. All
						relevant parties have been notified.
					</Text>
				</motion.div>
			</Flex>

			{/* Fixed Bottom Buttons - Mobile optimized */}
			<motion.div
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, delay: 1.0 }}
				style={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					right: 0,
					backgroundColor: 'white',
					borderTop: '1px solid var(--mantine-color-gray-3)',
					padding: '20px',
					paddingBottom: '32px',
					zIndex: 1000,
					boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
				}}
			>
				<Flex direction="column" gap="md">
					<Button variant="filled" size="md" fullWidth onClick={handleProcessAnother}>
						Process Another Load
					</Button>
					<Button variant="outline" bg="gray" size="md" fullWidth onClick={handleReturnToFacility}>
						Return to Facility Dashboard
					</Button>
				</Flex>
			</motion.div>
		</Flex>
	);
};
